import { Link } from 'react-router-dom';
import { FaFacebookF, FaTwitter, FaInstagram, FaEdit } from 'react-icons/fa';
import React, { useContext, useState, useEffect } from 'react';
import { Context } from '../../../Context';
import { Modal } from 'flowbite-react';
import { toast } from 'react-toastify';
import { API, Storage } from 'aws-amplify';

const Footer = () => {
  const { institutionData, setInstitutionData } = useContext(Context);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editData, setEditData] = useState({
    logoUrl: '',
    companyName: '',
    tagLine1: '',
    usefulLinks: [],
    address: '',
    phone: '',
    socialMediaLinks: [],
    copyrightText: ''
  });
  const [logoFile, setLogoFile] = useState(null);

  const UserCtx = useContext(Context);
  const isAdmin = UserCtx.userData.userType === "admin";

  // Initialize with context data
  useEffect(() => {
    if (institutionData) {
      setEditData({
        logoUrl: institutionData.logoUrl || '',
        companyName: institutionData.companyName || '',
        tagLine1: institutionData.tagLine1 || '',
        usefulLinks: institutionData.usefulLinks || [],
        address: institutionData.Query_Address || '',
        phone: institutionData.Query_PhoneNumber || '',
        socialMediaLinks: institutionData.socialMediaLinks || [],
        copyrightText: institutionData.copyrightText ||
          `© ${new Date().getFullYear()} ${institutionData.companyName || ''}. All rights reserved.`
      });
    }
  }, [institutionData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditData(prev => ({ ...prev, logoUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogoToS3 = async (file) => {
    try {
      const fileName = `logos/${institutionData.institution}-${Date.now()}-${file.name}`;
      await Storage.put(fileName, file, {
        contentType: file.type,
        level: 'public'
      });
      const fileUrl = await Storage.get(fileName);
      return fileUrl.split('?')[0];
    } catch (error) {
      console.error("Error uploading logo to S3:", error);
      throw error;
    }
  };

  const handleLinkChange = (index, field, value) => {
    const newLinks = [...editData.usefulLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setEditData(prev => ({ ...prev, usefulLinks: newLinks }));
  };

  const addNewLink = () => {
    setEditData(prev => ({
      ...prev,
      usefulLinks: [...prev.usefulLinks, { title: 'New Link', url: '/' }]
    }));
  };

  const removeLink = (index) => {
    const newLinks = [...editData.usefulLinks];
    newLinks.splice(index, 1);
    setEditData(prev => ({ ...prev, usefulLinks: newLinks }));
  };

  const handleSocialLinkChange = (platform, url) => {
    const newSocialLinks = [...editData.socialMediaLinks];
    const existingIndex = newSocialLinks.findIndex(link => link.platform === platform);

    if (existingIndex >= 0) {
      newSocialLinks[existingIndex] = { platform, url };
    } else {
      newSocialLinks.push({ platform, url });
    }

    setEditData(prev => ({ ...prev, socialMediaLinks: newSocialLinks }));
  };

  const handleSave = async () => {
    try {
      setIsUploading(true);

      let finalLogoUrl = editData.logoUrl;
      if (logoFile) {
        finalLogoUrl = await uploadLogoToS3(logoFile);
      }

      const updatedData = {
        ...institutionData,
        logoUrl: finalLogoUrl,
        companyName: editData.companyName,
        tagLine1: editData.tagLine1,
        usefulLinks: editData.usefulLinks,
        Query_Address: editData.address,
        Query_PhoneNumber: editData.phone,
        socialMediaLinks: editData.socialMediaLinks,
        copyrightText: editData.copyrightText
      };

      setInstitutionData(updatedData);

      await API.put('main', `/admin/editable-ui/${institutionData.institution}?query=footer`, {
        body: {
          logoUrl: finalLogoUrl,
          companyName: editData.companyName,
          tagLine1: editData.tagLine1,
          usefulLinks: editData.usefulLinks,
          Query_Address: editData.address,
          Query_PhoneNumber: editData.phone,
          socialMediaLinks: editData.socialMediaLinks,
          copyrightText: editData.copyrightText
        }
      });

      toast.success("Changes saved successfully!", {
        position: "top-right",
        autoClose: 3000,
        style: {
          background: "#16A34A",
          color: "#ffffff",
          borderRadius: "8px",
          padding: "12px",
        },
      });

      setLogoFile(null);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save changes. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      logoUrl: institutionData.logoUrl || '',
      companyName: institutionData.companyName || '',
      tagLine1: institutionData.tagLine1 || '',
      usefulLinks: institutionData.usefulLinks || [],
      address: institutionData.Query_Address || '',
      phone: institutionData.Query_PhoneNumber || '',
      socialMediaLinks: institutionData.socialMediaLinks || [],
      copyrightText: institutionData.copyrightText ||
        `© ${new Date().getFullYear()} ${institutionData.companyName || ''}. All rights reserved.`
    });
    setLogoFile(null);
    toast.info("Changes discarded.", {
      position: "top-right",
      autoClose: 3000,
      style: {
        background: "#722F37",
        color: "#ffffff",
        borderRadius: "8px",
        padding: "12px",
      },
    });
    setIsEditing(false);
  };

  // Combine mandatory links with institution's useful links
  const quickLinks = [
    { title: "Login", url: "/login" },
    { title: "Products", url: "/product" },
    ...(editData.usefulLinks || [])
  ];

  const socialIcons = {
    Facebook: FaFacebookF,
    Twitter: FaTwitter,
    Instagram: FaInstagram
  };

  const getSocialLink = (platform) => {
    return editData.socialMediaLinks?.find(
      link => link.platform === platform
    )?.url;
  };

  const isFormValid = () => {
    const expectedPrefixes = {
      Instagram: 'https://www.instagram.com/',
      Facebook: 'https://www.facebook.com/',
      Twitter: 'https://twitter.com/',
    };

    for (const [platform, expected] of Object.entries(expectedPrefixes)) {
      const url = getSocialLink(platform);
      if (url && !url.startsWith(expected)) {
        return false;
      }
    }

    return !!editData.logoUrl;
  };


  return (
    <footer className="bg-[#2C2C2C] text-white relative">
      <div className="absolute top-4 right-4 z-20">
        {isAdmin && (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-[var(--primary-color)] text-white px-4 py-2 rounded-full font-medium shadow-lg transition-all duration-300 flex items-center gap-2"
          >
            <FaEdit className="text-md" />
            <span className="text-md">Edit</span>
          </button>
        )}
      </div>

      <div className="max-w-[1440px] mx-auto px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div>
            <Link to="/" className="flex items-center gap-2.5 mb-6">
              <img
                src={editData.logoUrl}
                alt={`${editData.companyName} Logo`}
                className="w-14 h-14 object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  // e.target.src = 'https://via.placeholder.com/56';
                }}
              />
              <span className="text-[#E8C9A3] text-base tracking-tight">
                {editData.companyName}
              </span>
            </Link>
            <p className="text-white/60 text-sm">
              {editData.tagLine1}
            </p>
          </div>

          {/* Quick Links Section */}
          <div>
            <h3 className="text-white font-medium mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={`${link.url}-${index}`}>
                  <Link
                    to={link.url}
                    className="text-white/60 hover:text-white transition-colors duration-300"
                  >
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Section */}
          <div>
            <h3 className="text-white font-medium mb-4">Contact</h3>
            <ul className="space-y-3 text-white/60">
              <li>{editData.address || 'No address provided'}</li>
              <li>{editData.phone || 'No phone number provided'}</li>
            </ul>
          </div>

          {/* Social Media Section */}
          <div>
            <h3 className="text-white font-medium mb-4">Follow Us</h3>
            <div className="flex gap-4">
              {Object.entries(socialIcons).map(([platform, Icon]) => {
                const socialLink = getSocialLink(platform);
                return (
                  <a
                    key={platform}
                    href={socialLink || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${socialLink ? 'bg-white/10 hover:bg-white/20' : 'bg-white/5 cursor-default'}`}
                  >
                    <Icon className="text-white" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center text-white/40 text-sm">
          {editData.copyrightText}
        </div>
      </div>

      {/* Flowbite Modal */}
      <Modal show={isEditing} onClose={handleCancel} size="lg">
        <Modal.Header className="border-b p-4">
          Edit Footer Content
        </Modal.Header>
        <Modal.Body className="p-4 space-y-4">
          {/* Brand Info */}
          {/* Brand Info */}
          <div className="space-y-2">
            <h3 className="font-medium">
              Brand Logo <span className="text-red-500">*</span>
            </h3>
            <div className="flex items-center gap-4">
              {editData.logoUrl && (
                <img
                  src={editData.logoUrl}
                  alt="Logo Preview"
                  className="w-16 h-16 object-contain rounded"
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="text-sm"
                disabled={isUploading}
              />
            </div>
            {!editData.logoUrl && (
              <p className="text-sm text-red-500 mt-1">Brand logo is required.</p>
            )}
          </div>


          {/* Quick Links */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Quick Links</h3>
              <button
                onClick={addNewLink}
                className="text-sm text-blue-600 hover:text-blue-800"
                disabled={isUploading}
              >
                + Add Link
              </button>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {editData.usefulLinks.map((link, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={link.title}
                    onChange={(e) => handleLinkChange(index, 'title', e.target.value)}
                    placeholder="Link text"
                    className="flex-1 p-2 text-sm border rounded"
                    disabled={isUploading}
                  />
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                    placeholder="/path"
                    className="flex-1 p-2 text-sm border rounded"
                    disabled={isUploading}
                  />
                  <button
                    onClick={() => removeLink(index)}
                    className="p-2 text-red-600 hover:text-red-800"
                    disabled={isUploading}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Social Links */}
          <div className="space-y-2">
            <h3 className="font-medium">Social Links</h3>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(socialIcons).map(([platform]) => {
                const value = getSocialLink(platform) || '';
                // Define expected base URLs
                const expectedPrefixes = {
                  Instagram: 'https://www.instagram.com/',
                  Facebook: 'https://www.facebook.com/',
                  Twitter: 'https://twitter.com/',
                };

                const expected = expectedPrefixes[platform];
                const isInvalid =
                  value &&
                  expected &&
                  !value.startsWith(expected);

                return (
                  <div key={platform} className="space-y-1">
                    <label className="text-sm">{platform}</label>
                    <input
                      type="url"
                      value={value}
                      onChange={(e) => handleSocialLinkChange(platform, e.target.value)}
                      placeholder={`${platform} URL`}
                      className={`w-full p-2 text-sm border rounded ${isInvalid ? 'border-red-500' : 'border-gray-300'
                        }`}
                      disabled={isUploading}
                    />
                    {isInvalid && (
                      <p className="text-xs text-red-500">
                        URL must start with {expected}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-t p-4">
          <div className="flex justify-center w-full">
            <button
              onClick={handleSave}
              disabled={isUploading || !isFormValid()}
              className={`text-white font-medium rounded-lg text-sm px-5 py-2.5 text-center focus:outline-none 
    ${isUploading || !isFormValid()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primaryColor hover:bg-primaryColor/90 focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-800'
                }`}
            >
              {isUploading ? 'Saving...' : 'Save'}
            </button>

          </div>
        </Modal.Footer>
      </Modal>
    </footer >
  );
};

export default Footer;